import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createOrderMethod,
    deleteOrderMethod,
    createVendor,
    fetchVendor,
    fetchVendorList,
    updateOrderMethod,
    updateVendor,
    type CreateOrderMethodBody,
    type CreateVendorBody,
    type UpdateOrderMethodBody,
    type UpdateVendorBody,
    type VendorListItem
} from '../services/vendors';
import {
    BILLING_CHARGES_KEY,
    BILLING_PURCHASE_ORDERS_KEY,
    BILLING_VENDORS_KEY,
    PRODUCTS_KEY,
    VENDOR_DETAIL_KEY,
    VENDORS_LIST_KEY
} from './queryKeys';

const STALE_TIME_MS = 60 * 1000;

// Stable identity: the fallback feeds render-time lists.
const EMPTY_VENDORS: VendorListItem[] = [];

export function useVendorList() {
    const { data, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey: VENDORS_LIST_KEY,
        queryFn: fetchVendorList,
        staleTime: STALE_TIME_MS
    });

    return { vendors: data ?? EMPTY_VENDORS, isLoading, isFetching, isError, refetch };
}

export function useVendor(id: string | null) {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: VENDOR_DETAIL_KEY(id ?? ''),
        queryFn: () => fetchVendor(id as string),
        staleTime: STALE_TIME_MS,
        enabled: Boolean(id)
    });

    return {
        vendor: data,
        isLoading: isLoading && Boolean(id),
        isError,
        error,
        refetch
    };
}

// One base key: a write refreshes the list, the open drawer and the
// purchase-order vendor filter in a single invalidation.
export function useCreateVendor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateVendorBody) => createVendor(body),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: BILLING_VENDORS_KEY });
        }
    });
}

export function useUpdateVendor() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: UpdateVendorBody }) =>
            updateVendor(id, body),
        onSuccess: (result) => {
            queryClient.setQueryData(VENDOR_DETAIL_KEY(result.vendor.id), result.vendor);
            void queryClient.invalidateQueries({ queryKey: VENDORS_LIST_KEY });
            void queryClient.invalidateQueries({ queryKey: BILLING_VENDORS_KEY, exact: true });
            // PO rows and the charge drawer carry vendorName, so a rename is stale
            // until they refetch.
            void queryClient.invalidateQueries({ queryKey: [BILLING_PURCHASE_ORDERS_KEY] });
            void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
        }
    });
}

// A method write changes the vendor's rollup and the open drawer, so both
// refresh; PO rows and charges carry method-derived channel labels too.
function useOrderMethodMutation<TVariables>(
    mutationFn: (variables: TVariables) => Promise<unknown>
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: BILLING_VENDORS_KEY });
            void queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
        }
    });
}

export function useCreateOrderMethod() {
    return useOrderMethodMutation(
        ({ vendorId, body }: { vendorId: string; body: CreateOrderMethodBody }) =>
            createOrderMethod(vendorId, body)
    );
}

export function useDeleteOrderMethod() {
    return useOrderMethodMutation(
        ({ vendorId, methodId }: { vendorId: string; methodId: string }) =>
            deleteOrderMethod(vendorId, methodId)
    );
}

export function useUpdateOrderMethod() {
    return useOrderMethodMutation(
        ({
            vendorId,
            methodId,
            body
        }: {
            vendorId: string;
            methodId: string;
            body: UpdateOrderMethodBody;
        }) => updateOrderMethod(vendorId, methodId, body)
    );
}
